import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PopupModal from "../../PopupModal";
import {
    Alert,
    Badge,
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
} from "@mui/material";
import { FaCheckCircle, FaComments } from "react-icons/fa";
import { displayNotification } from "helpers";
import { uniqueId } from "lodash";
import {
    useCreateFormElementCommentMutation,
    useLazyGetFormElementCommentsQuery,
} from "modules/pvs_activities/redux/service/formComments";
import { FormDataContext } from "../../contexts/FormContext";
import { useSelector } from "react-redux";
import moment from "moment";
import TextAreaField from "../../core/TextAreaField";
import Heading from "../../core/Heading";
import DropdownField from "../../core/DropdownField";
import CommentElement from "../CommentElement";
import { initialCommentFormFields } from "modules/pvs_activities/constants";

const initialFilters = {
    is_resolved: "",
};

const defaultProps = {
    sectionId: "",
    elementId: "",
    ccId: "",
    children: "",
    className: "",
    commentsCount: 0,
    setCommentsCount: () => {},
    label: null,
};

const CommentModal = (rawProps) => {
    const props = { ...defaultProps, ...rawProps };

    const modalRef = useRef();
    const formDataContext = useContext(FormDataContext);
    const authData = useSelector((s) => s.auth.data);

    const [form, setForm] = useState({ ...initialCommentFormFields });
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState({ ...initialFilters });
    const [commentsTrigger, commentsRes] = useLazyGetFormElementCommentsQuery();
    const [createFormElementComment, createCommentRes] = useCreateFormElementCommentMutation();

    useEffect(() => {
        if (modalRef.current?.isOpen) {
            props.setCommentsCount(data.length);
        }
    }, [data, modalRef.current?.isOpen]);

    useEffect(() => {
        if (formDataContext.mode === "data_entry") {
            commentsTrigger({
                elementId: props.elementId,
                movementId: formDataContext.movementId,
                ccId: props.ccId,
                isResolved: filters.is_resolved,
            });
        }
    }, [filters.is_resolved, formDataContext.mode]);

    useEffect(() => {
        if (commentsRes?.status !== "uninitialized") {
            if (commentsRes?.isError) {
                displayNotification("error", "Error loading comments");
                console.error({ error: commentsRes.error });
            }
            if (commentsRes.isSuccess && !commentsRes.isLoading) {
                setData(commentsRes.data);
            }
        }
    }, [commentsRes]);

    useEffect(() => {
        if (createCommentRes?.isError) {
            displayNotification("error", "Error creating comment");
            console.error({ error: createCommentRes.error });
        }
        if (createCommentRes.isSuccess) {
            setForm({ ...initialCommentFormFields });
            displayNotification("success", "Successfully created comment");
        }
    }, [createCommentRes]);

    const handleSave = (e) => {
        e.preventDefault();
        if (!form.content.trim()) {
            displayNotification("error", "Comment cannot be empty");
            return;
        }    
        if (formDataContext.mode === "preview") {
            setData([
                {
                    ...form,
                    user_id: authData.id,
                    user_name: authData.name,
                    role_name: authData?.role?.role_name ?? "-",
                    element_id: props.elementId,
                    is_resolved: false,
                    created_date: moment().format("DD-MMMM-YYYY (hh:mm A)"),
                    id: uniqueId(),
                },
                ...data,
            ]);
            setForm({ ...initialCommentFormFields });
            displayNotification("success", "Successfully saved comment");
        } else {
            createFormElementComment({
                ...form,
                user_id: authData.id,
                element_id: props.elementId,
                template_id: formDataContext.templateId,
                section_id: formDataContext.sectionId,
                movement_id: formDataContext.movementId,
                cc_id: props.ccId,
            });
        }
    };

    const finalData = useMemo(() => {
        return formDataContext.mode === "preview"
            ? data.filter((r) => (filters.is_resolved === "" ? true : !!+r.is_resolved === !!+filters.is_resolved))
            : data;
    }, [data, filters.is_resolved]);

    // Only For Preview
    const handleModifyComments = (callback) => {
        const tmp = callback(data);
        if (tmp) setData(tmp);
    };

    return (
        <>
            <PopupModal
                ref={modalRef}
                title="Comments"
                triggers={({openModal}) => (
                    <Badge
                        color="themeTwo"
                        badgeContent={props.commentsCount || finalData.length}
                        sx={{ "& .MuiBadge-badge": { border: "1px solid #faccdd" } }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                formDataContext.mode === "data_entry" &&
                                    commentsTrigger({
                                        elementId: props.elementId,
                                        movementId: formDataContext.movementId,
                                        ccId: props.ccId,
                                        isResolved: filters.is_resolved,
                                    });
                                openModal();
                            }}
                            className={`${props.className} ms-2 fs-8 my-auto d-flex align-items-center`}
                            size="small"
                        >
                            {props.label ? (
                                props.label
                            ) : (
                                <>
                                    <FaComments />
                                    <span className="ms-1">Comments</span>
                                </>
                            )}
                        </Button>
                    </Badge>
                )}
                size="xl"
            >
                <>
                    {formDataContext.isReadOnly === false && (
                        <>
                            <div className="mb-2">
                                <FormGroup row sx={{ mt: "5px", width: "100%" }}>
                                    <TextAreaField
                                        value={form.content}
                                        onChange={(value) => setForm({ ...form, content: value })}
                                        label={"Comment"}
                                        placeholder={"Write your text here..."}
                                        minRows={4}
                                        maxRows={4}
                                        size={"small"}
                                        multiline
                                        fullWidth={false}
                                        marginTop={0}
                                        sx={{ flexGrow: 1 }}
                                        onKeyDown={(e) => e.keyCode === 13 && handleSave(e)}
                                        InputProps={{
                                            startAdornment: !["FT_LTR"].includes(formDataContext.formType) ? (
                                                <InputAdornment position="start">
                                                    <FormControl
                                                        value={form.type}
                                                        onChange={(e) => setForm({ ...form, type: e.target.value })}
                                                        sx={{ borderRight: '1px solid #ccc' }}
                                                    >
                                                        <RadioGroup
                                                            aria-labelledby="comment-modal-type"
                                                            defaultValue="comment"
                                                            name="radio-buttons-group"
                                                            sx={{ gap: "4px" }}
                                                        >
                                                            <FormControlLabel
                                                                value="comment"
                                                                control={<Radio size="small" />}
                                                                label="Comment"
                                                                sx={{ "& .MuiButtonBase-root": { py: 0 } }}
                                                            />
                                                            <FormControlLabel
                                                                value="question"
                                                                control={<Radio size="small" />}
                                                                label="Question"
                                                                sx={{ "& .MuiButtonBase-root": { py: 0 } }}
                                                            />
                                                        </RadioGroup>
                                                    </FormControl>
                                                </InputAdornment>
                                            ) : null,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        variant="contained"
                                                        color="success"
                                                        onClick={handleSave}
                                                        disableElevation
                                                        sx={{
                                                            height: 45,
                                                            width: 45,
                                                            fontSize: "1.75rem",
                                                        }}
                                                    >
                                                        <FaCheckCircle />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormGroup>
                            </div>
                            <div className="d-flex mb-2">
                                <Box sx={{ display: "flex" }}>
                                    <Heading label="Filters:" sx={{ mr: 1, my: 'auto' }} />
                                    <DropdownField
                                        plain={true}
                                        label={"Select Status"}
                                        // defaultOptionLabel={"All"}
                                        value={filters.is_resolved===''?"All":filters.is_resolved}
                                        onChange={(val) => setFilters({ ...filters, is_resolved: val === 'All'?'':val })}
                                        hasOptions={true}
                                        options={[
                                            {value: 'All', label: 'All'},
                                            { value: "0", label: "Unresolved" },
                                            { value: "1", label: "Resolved" },
                                        ]}
                                        size={"small"}
                                    />
                                </Box>
                            </div>
                        </>
                    )}
                    {finalData.length > 0 ? (
                        finalData.map((r) => (
                            <CommentElement
                                key={r.id}
                                elementId={props.elementId}
                                data={r}
                                onModifyComments={handleModifyComments}
                            />
                        ))
                    ) : (
                        <Alert severity="info" sx={{ textAlign: "center" }}>
                            No Comments Found
                        </Alert>
                    )}
                </>
            </PopupModal>
        </>
    );
};

export default CommentModal;
